import type { StyleRule } from '@vanilla-extract/css';

import Borders from '@dynatrace/strato-design-tokens/borders';
import Colors from '@dynatrace/strato-design-tokens/colors';
import Easings from '@dynatrace/strato-design-tokens/easings';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import Timings from '@dynatrace/strato-design-tokens/timings';

import { dtVisualRefreshEnabled } from '../../core/components/app-root/AppRoot.sty.js';

export const vrResizeHandleContainerOverrides = {
  [`.${dtVisualRefreshEnabled} &`]: {
    borderRadius: 0,
    height: '100%',
    width: Spacings.Size8,
    left: 0,
    transition: 'none',
    backgroundColor: 'transparent',
    marginInline: `calc(${Spacings.Size8} / -2)`,
    zIndex: 1,
  },
  [`.${dtVisualRefreshEnabled} &::before`]: {
    content: '""',
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: Borders.Width.Default,
    backgroundColor: Colors.Border.Neutral.Subdued,
    pointerEvents: 'none' as const,
  },
  [`.${dtVisualRefreshEnabled} &:hover::before`]: {
    backgroundColor: Colors.Border.Neutral.SubduedHover,
  },
  [`.${dtVisualRefreshEnabled} &:active::before`]: {
    backgroundColor: Colors.Border.Neutral.SubduedActive,
  },
};

export const vrResizeHandleFocusOverrides = {
  backgroundColor: Colors.Border.Neutral.Accent,
  width: Borders.Width.Emphasized,
};

export const resizeSeparatorStyles = {
  backgroundColor: Colors.Border.Neutral.Accent,
  border: 'none',
  borderRadius: Borders.Radius.Field.Default,
  cursor: 'col-resize',
  height: Spacings.Size36,
  left: '3px',
  margin: 0,
  padding: 0,
  touchAction: 'none',
  transition: `height ${Timings.Base.Default} ${Easings.FastInSlowOut}`,
  width: '2px',
  selectors: {
    '&:focus-visible': { width: '100%' },
    '&:hover&:focus-visible': { width: '2px' },
    '&:focus-visible, &:hover': {
      height: Spacings.Size48,
      backgroundColor: Colors.Border.Neutral.AccentHover,
    },
    '&:after': {
      content: '""',
      display: 'block',
      height: '100%',
      left: '50%',
      position: 'absolute',
      top: 0,
      transform: 'translateX(-50%)',
      userSelect: 'none',
      width: '100%',
    },
    // The separator is invisible under VR — the container's ::before line is the visual divider —
    // but it still owns the resize role, the focus and the drag gesture, so it keeps the
    // container's box rather than collapsing to 0. A zero-width box is reported as hidden by
    // hit-testing tools even though the :after pseudo-element covers the track.
    [`.${dtVisualRefreshEnabled} &`]: {
      width: Spacings.Size8,
      height: '100%',
      borderRadius: 0,
      backgroundColor: 'transparent',
      cursor: 'ew-resize',
      // The shared focus ring would now have a box to draw on. Keyboard focus is indicated by the
      // container's ::before turning into the accent line instead.
      boxShadow: 'none',
    },
    // The rules above paint an accent bar on hover and grow the separator on focus. Under VR both
    // states are drawn on the container's ::before instead, so the separator stays transparent.
    [`.${dtVisualRefreshEnabled} &:hover, .${dtVisualRefreshEnabled} &:focus-visible, .${dtVisualRefreshEnabled} &:hover&:focus-visible`]:
      {
        width: Spacings.Size8,
        height: '100%',
        backgroundColor: 'transparent',
      },
  },
} satisfies StyleRule;
